"use client";

import React from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { sectionVariants } from "@/types";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "../ui/badge";
import Image from '@/components/ui/Image';

interface Feature20Props { 
  darkMode?: boolean; 
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  badge?: string;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  items?: {
    image: string;
    imageAltText: string;
    title: string;
    content: string;
    link: string;
  }[];
  attributes?: React.HTMLProps<HTMLElement>;
}

const Feature20 = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Feature 20",
  themeChangeOnScroll = false,
  badge,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  items,
  attributes,
}: Feature20Props) => {

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="feature20"
      attributes={attributes}
    >

      <div className="container">
        <div className="flex flex-col justify-between gap-8 lg:flex-row" data-aos="fade-up">

            <div className="w-full lg:w-1/3">
              {badge && (
                <Badge variant="outline" bold className="mb-6">
                  {badge}
                </Badge>
              )}
              {title && ( 
                <h3>
                  {parse(title)}
                </h3>
              )}
              {content && (
                <div className="text-muted-foreground mt-8">
                  {parse(content)}
                </div>
              )}
              {hasButton && (
                <div className="flex-col md:flex-row gap-4 mt-4 hidden lg:flex">
                  <div className="flex flex-col">
                    <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                      <Link href={buttonLink ?? ""}>
                        {buttonText}
                      </Link>
                    </Button>
                    {microCopy && (
                      <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                        {microCopy}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {items?.slice(0, 2).map((item, idx) => (
              item.link ? (
                <a href={item.link ?? ""} className="w-full lg:w-1/3 flex flex-col gap-4" key={idx}>
                  {item.image && (
                    <Image src={item.image} alt={item.imageAltText} title={item.imageAltText} width={438} height={285} className="rounded-xl object-cover aspect-[438/285] mb-2 w-full" />
                  )}
                  {item.title && (
                    <h4 className="line-clamp-3">
                      {parse(item.title)}
                    </h4>
                  )}
                  {item.content && (
                    <div className="paragraph-lg text-muted-foreground line-clamp-2 hidden sm:block">
                      {parse(item.content)}
                    </div>
                  )}
                </a>
              ) : (
                <div className="w-full lg:w-1/3 flex flex-col gap-4" key={idx}>
                  {item.image && (
                    <Image src={item.image} alt={item.imageAltText} title={item.imageAltText} width={438} height={285} className="rounded-xl object-cover aspect-[438/285] mb-2 w-full" />
                  )}
                  {item.title && (
                    <h4 className="line-clamp-3">
                      {parse(item.title)}
                    </h4>
                  )}
                  {item.content && (
                    <div className="paragraph-lg text-muted-foreground line-clamp-2">
                      {parse(item.content)}
                    </div>
                  )}
                </div>
              )
            )
          )}
        </div>
        {hasButton && (
          <div className="flex-col flex lg:hidden mt-8">
            <div className="flex flex-col">
              <Button asChild variant={buttonStyle} size="lg" className="w-full sm:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <p className="paragraph-sm text-muted-foreground text-center md:text-left mt-3">
                  {microCopy}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </SectionWrapper>
  )
};

export { Feature20 };